import { formatPhone } from '@fundline/shared/phone';
import { Link } from '@tanstack/react-router';
import { useEffect, useId, useMemo, useState } from 'react';
import { Alert } from '../../components/ui/Alert.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { CONTROL, SelectField } from '../../components/ui/Field.tsx';
import { EmptyState, PageHeader, Spinner } from '../../components/ui/Layout.tsx';
import { TagChip } from '../../components/ui/TagChip.tsx';
import { useWorkspaceClaims } from '../../lib/auth.tsx';
import { useMembersQuery } from '../members/use-members.ts';
import { useTagsQuery } from '../settings/use-tags.ts';
import {
  type MerchantListFilters,
  type MerchantListItem,
  useMerchantListQuery,
} from './use-merchants.ts';

export interface MerchantsPageProps {
  filters: MerchantListFilters;
  onFiltersChange: (next: MerchantListFilters) => void;
}

/** Merchant list: search, filters, load more. Reps only ever get their own merchants (RLS). */
export function MerchantsPage({ filters, onFiltersChange }: MerchantsPageProps) {
  const { workspaceId, role } = useWorkspaceClaims();
  const list = useMerchantListQuery(workspaceId, filters);
  const members = useMembersQuery(workspaceId);
  const tags = useTagsQuery(workspaceId);
  const searchId = useId();
  const [q, setQ] = useState(filters.q);

  // Debounced search: the URL (and the query) follows the box after a short pause.
  useEffect(() => {
    if (q === filters.q) return;
    const t = setTimeout(() => onFiltersChange({ ...filters, q }), 300);
    return () => clearTimeout(t);
  }, [q, filters, onFiltersChange]);

  const names = useMemo(
    () => new Map((members.data ?? []).map((m) => [m.userId, m.fullName || m.email])),
    [members.data],
  );
  const tagById = useMemo(() => new Map((tags.data ?? []).map((t) => [t.id, t])), [tags.data]);
  const rows = list.data?.pages.flat() ?? [];
  const filtered = Boolean(filters.q || filters.rep || filters.tag);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Merchants"
        description={
          role === 'admin' ? 'Every merchant in the workspace.' : 'Merchants assigned to you.'
        }
        actions={
          <>
            {role === 'admin' ? (
              <Link
                to="/merchants/imports"
                className="fl-btn fl-btn--secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
              >
                Import
              </Link>
            ) : null}
            <Link
              to="/merchants/new"
              className="fl-btn fl-btn--primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
            >
              New merchant
            </Link>
          </>
        }
      />

      <section className="flex flex-col gap-3" aria-label="Filters">
        <div className="flex flex-col gap-1">
          <label htmlFor={searchId} className="callout font-medium text-ink">
            Search
          </label>
          <input
            id={searchId}
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Business, owner, email or phone"
            className={CONTROL}
          />
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          {role === 'admin' ? (
            <SelectField
              label="Assigned to"
              value={filters.rep ?? ''}
              onChange={(e) => onFiltersChange({ ...filters, rep: e.target.value || null })}
              options={[
                { value: '', label: 'Everyone' },
                ...(members.data ?? [])
                  .filter((m) => m.status !== 'invited')
                  .map((m) => ({ value: m.userId, label: m.fullName || m.email })),
              ]}
            />
          ) : null}
          <SelectField
            label="Tag"
            value={filters.tag ?? ''}
            onChange={(e) => onFiltersChange({ ...filters, tag: e.target.value || null })}
            options={[
              { value: '', label: 'Any tag' },
              ...(tags.data ?? []).map((t) => ({ value: t.id, label: t.name })),
            ]}
          />
          <SelectField
            label="Show"
            value={filters.archived ? 'archived' : 'active'}
            onChange={(e) =>
              onFiltersChange({ ...filters, archived: e.target.value === 'archived' })
            }
            options={[
              { value: 'active', label: 'Active merchants' },
              { value: 'archived', label: 'Archived merchants' },
            ]}
          />
        </div>
      </section>

      <section className="flex flex-col gap-3" aria-label="Merchant list">
        {list.isPending ? <Spinner label="Loading merchants" /> : null}
        {list.isError ? <Alert tone="error">{list.error.message}</Alert> : null}
        {list.isSuccess && rows.length === 0 ? (
          filtered || filters.archived ? (
            <EmptyState title="No merchants match">Try another search or filter.</EmptyState>
          ) : (
            <EmptyState title="No merchants yet">
              Add one with <span className="font-medium">New merchant</span>. Imports arrive in a
              later milestone.
            </EmptyState>
          )
        ) : null}
        {rows.length > 0 ? (
          <ul className="flex flex-col divide-y divide-hairline rounded-lg border border-hairline bg-surface-raised">
            {rows.map((m) => (
              <MerchantRow
                key={m.id}
                merchant={m}
                repName={role === 'admin' ? (names.get(m.assignedRepId) ?? '') : null}
                tags={m.tagIds.flatMap((id) => {
                  const t = tagById.get(id);
                  return t ? [t] : [];
                })}
              />
            ))}
          </ul>
        ) : null}
        {list.hasNextPage ? (
          <div>
            <Button
              variant="secondary"
              busy={list.isFetchingNextPage}
              onClick={() => void list.fetchNextPage()}
            >
              Load more
            </Button>
          </div>
        ) : null}
      </section>
    </div>
  );
}

function MerchantRow({
  merchant: m,
  repName,
  tags,
}: {
  merchant: MerchantListItem;
  repName: string | null;
  tags: Array<{ id: string; name: string; color: string }>;
}) {
  const title = m.businessName ?? m.ownerName ?? formatPhone(m.phone);
  const subtitle = [m.businessName ? m.ownerName : null, formatPhone(m.phone), m.state]
    .filter((x): x is string => Boolean(x) && x !== title)
    .join(' · ');
  return (
    <li data-testid="merchant-row">
      <Link
        to="/merchants/$merchantId"
        params={{ merchantId: m.id }}
        className="flex flex-col gap-1 p-4 hover:bg-surface focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus-ring sm:flex-row sm:items-center sm:justify-between"
      >
        <span className="min-w-0">
          <span className="block truncate font-medium text-ink">{title}</span>
          <span className="block truncate callout text-ink-muted">{subtitle}</span>
        </span>
        <span className="flex min-w-0 flex-wrap items-center gap-1 sm:justify-end">
          {tags.slice(0, 3).map((t) => (
            <TagChip key={t.id} name={t.name} color={t.color} />
          ))}
          {tags.length > 3 ? (
            <span className="footnote text-ink-muted">+{tags.length - 3}</span>
          ) : null}
          {repName ? (
            <span className="ml-1 truncate footnote text-ink-muted">{repName}</span>
          ) : null}
        </span>
      </Link>
    </li>
  );
}
